// Compose modal — To/Cc/Bcc with chip-style addresses, attachments, schedule, draft.

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { api } from "../../shared/api/client";
import { toast } from "../../shared/stores/toasts";
import { RichEditor, asImageFile, isImageFile, type RichEditorHandle } from "./RichEditor";
import { I } from "../../shared/Icon";
import { chunkedUpload, fileSource } from "../docstudio/chunkUpload";
import type { MailDetail, Signature } from "./types";
import { SIG_SEPARATOR, formatBytes, renderMailHtml, signatureHtml, splitMailText } from "./types";

type Recipient = { id: number; name: string; email: string };

// 받는사람·참조·숨은참조 칸 사이에서 주소 칩을 끌어 옮기기 위한 식별자.
// dataTransfer 의 사용자 정의 형식 이름은 브라우저가 소문자로 바꾸므로 처음부터 소문자로 둔다.
type RecipientBox = "to" | "cc" | "bcc";
const CHIP_DND_TYPE = "application/x-timbox-recipient";

type Props = {
  open: boolean;
  onClose: () => void;
  onSent: () => void;
  initial?: Partial<MailDetail> & { in_reply_to_id?: number };
  draftId?: number;       // existing draft to edit
  signature?: Signature | null;
  asPage?: boolean;       // render as a full-page composer instead of a floating modal
};

type UploadedAtt = { id: number; file_name: string; file_size: number; mime_type: string | null };
type FileLink = { id: number; url: string; file_name: string; file_size: number; expires_at: string };

/** 첨부 목록에 '링크로 보내기'를 권하는 기준. 물어보는 기준과 같은 선에 맞춘다 —
 *  더 일찍 뜨면 그 아래 크기에서도 계속 권하는 꼴이라 성가시다. */
const LINK_SUGGEST_BYTES = 30 * 1024 * 1024;

/** 이보다 커야 링크로 바꿀지 물어본다. 30MB 이하는 그냥 파일로 첨부한다(운영 방침).
 *
 *  참고로 첨부는 base64 로 실려 나가면서 약 1.37배 부푼다 — 30MB 는 회선에서 약 41MB 다.
 *  네이버·관공서처럼 10~20MB 에서 끊는 곳으로 보내면 거부될 수 있고, 그 거부는 한참 뒤
 *  반송메일로 오기 때문에 보낸메일함은 멀쩡해 보인다(실제로 40MB 가 552 로 반송됐다).
 *  그래도 매번 링크로 바꾸는 번거로움이 더 크다고 판단해 기준을 30MB 로 둔다. */
const LINK_AUTO_BYTES = 30 * 1024 * 1024;

// 서버(MAX_TOTAL_ATTACHMENT_BYTES)와 같은 값. 올리기 전에 걸러 시간을 아낀다.
const MAX_ATTACH_BYTES = 5 * 1024 * 1024 * 1024;   // 5GB

/** 메일에 실어 보낼 수 있는 첨부 총량의 한계.
 *
 *  우리 메일서버는 한 통을 100MB 까지 받는데, 첨부는 base64 로 실리며 약 1.37배 부푼다.
 *  그래서 실제로 통과하는 총량은 70MB 안쪽이고, 안전하게 50MB 로 끊는다.
 *  2026-09-23: 622MB 를 '그대로 첨부로 보내기' 로 보냈다가 메일서버가
 *  552 5.3.4 Message size exceeds fixed limit 로 거부했는데, 화면에는 '발송 완료' 로
 *  보였다. 나갈 수 없는 것은 애초에 고르지 못하게 막는다. */
const SMTP_HARD_LIMIT_BYTES = 50 * 1024 * 1024;

/** 큰 첨부를 실어 보내는 요청은 시간 제한을 두지 않는다(업로드와 같은 이유). */
const LONG_REQ = { timeout: 0 };

/** 대용량 첨부 안내 — 본문 맨 위에 얹는 카드(HTML)와 평문 두 벌.
 *
 *  메일 HTML 은 flex/grid 가 대부분 죽으므로 표로 짠다. 파일명은 왼쪽, 기한은
 *  오른쪽에 붙어야 하는데 그걸 안정적으로 만드는 방법이 table 뿐이다.
 *  평문에는 주소를 그대로 적는다 — HTML 을 못 읽는 곳에서는 그게 유일한 단서다. */
function largeAttachHtml(links: FileLink[], days: number): string {
  const total = links.reduce((n, l) => n + l.file_size, 0);
  const esc = (t: string) => t.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const rows = links.map((l) => {
    const until = new Date(l.expires_at);
    const ymd = `${until.getFullYear()}/${String(until.getMonth() + 1).padStart(2, "0")}/${String(until.getDate()).padStart(2, "0")}`;
    return '<table width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;margin:0 0 6px">'
      + '<tr><td style="font-size:13.5px;color:#111827;word-break:break-all;padding:0">'
      + `<a href="${esc(l.url)}" style="color:#111827;text-decoration:none">${esc(l.file_name)}</a>`
      + `<span style="color:#9ca3af;font-size:12px">&nbsp;&nbsp;${formatBytes(l.file_size)}</span>`
      + '</td>'
      + `<td align="right" style="white-space:nowrap;color:#ef4444;font-size:12.5px;padding:0 0 0 10px">~ ${ymd}</td>`
      + '</tr></table>';
  }).join("");
  return '<div style="border:1px solid #e5e7eb;border-radius:8px;padding:14px 16px;margin:0 0 18px;'
    + 'background:#ffffff;font-family:Pretendard Variable,Pretendard,-apple-system,'
    + 'BlinkMacSystemFont,Segoe UI,system-ui,Malgun Gothic,sans-serif">'
    + '<div style="font-size:14px;font-weight:700;color:#111827;margin:0 0 10px">대용량 첨부 '
    + `<span style="color:#4f46e5">${links.length}개</span>`
    + `<span style="color:#9ca3af;font-weight:400;font-size:12.5px">&nbsp;&nbsp;${formatBytes(total)}</span></div>`
    + '<div style="border:1px solid #eef0f3;border-radius:6px;padding:10px 12px;background:#fafbfc">'
    + rows
    + '</div>'
    + `<div style="font-size:12px;color:#9ca3af;margin:8px 0 0">기한이 있는 파일은 ${days}일 동안 보관됩니다.</div>`
    + '</div>';
}

function largeAttachText(links: FileLink[]): string {
  const NL = "\n";
  const lines = links.map((l) => {
    const until = new Date(l.expires_at).toLocaleDateString("ko-KR",
      { year: "numeric", month: "long", day: "numeric" });
    return `· ${l.file_name} (${formatBytes(l.file_size)})${NL}  ${l.url}${NL}  ${until}까지 받을 수 있습니다.`;
  });
  return `[대용량 첨부 ${links.length}개]${NL}${lines.join(NL)}${NL}${NL}`;
}

export function MailCompose({ open, onClose, onSent, initial, draftId, signature, asPage }: Props) {
  const [to, setTo] = useState<string[]>(initial?.to_addrs || []);
  const [cc, setCc] = useState<string[]>(initial?.cc_addrs || []);
  const [bcc, setBcc] = useState<string[]>(initial?.bcc_addrs || []);
  const [individual, setIndividual] = useState(!!initial?.individual);
  const [subject, setSubject] = useState(initial?.subject || "");
  const [body, setBody] = useState(initial?.body_text || initial?.body_html || "");
  // 다시 보내기로 열리면 서버가 복사해 둔 첨부가 initial 로 들어온다.
  const [atts, setAtts] = useState<UploadedAtt[]>(
    (initial?.attachments || []).map((a) => ({
      id: a.id, file_name: a.file_name, file_size: a.file_size, mime_type: a.mime_type,
    })),
  );
  const [busy, setBusy] = useState(false);
  // 예약 메일을 고치러 열면 예약 시각을 그대로 이어받는다(입력칸은 로컬 'YYYY-MM-DDTHH:mm').
  const [scheduledAt, setScheduledAt] = useState<string>(() => {
    if (!initial?.scheduled_at) return "";
    const d = new Date(initial.scheduled_at);
    const p = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  });
  const [showSchedule, setShowSchedule] = useState(!!initial?.scheduled_at);
  const inFlightDraftId = useRef<number | null>(draftId ?? null);
  // 자동 임시저장: 타이머는 항상 **최신** persistDraft 를 ref 로 부른다(아래 효과 참고).
  const persistRef = useRef<(silent?: boolean) => Promise<void>>(async () => {});
  const lastSavedKey = useRef<string>("");       // 마지막으로 저장한 내용의 지문 — 같으면 건너뛴다
  const savingRef = useRef(false);               // 저장이 겹치지 않게
  const [autoSave, setAutoSave] = useState<{ at: Date; ok: boolean; auto: boolean } | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const [aiOpen, setAiOpen] = useState(false);
  // AI 첨삭 창을 열 때 미리 채워 둘 지시문(맞춤법 검사 버튼용)
  const [aiPreset, setAiPreset] = useState<string | undefined>(undefined);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState<{ name: string; pct: number } | null>(null);
  // 대용량 첨부는 메일에 싣는 대신 임시 링크로 바꾼다. 만든 링크는 본문 맨 아래에
  // 모아 두고, 발송 직전에 본문 텍스트로 합친다.
  const [links, setLinks] = useState<FileLink[]>([]);
  const [linkDays, setLinkDays] = useState(7);
  const [linking, setLinking] = useState<number | null>(null);

  // 서식 편집기 사용 여부. 켜면 HTML 로, 끄면 평문으로 쓴다.
  // **기본은 서식**이다 — 표·색·이미지가 필요할 때 매번 켜는 것보다,
  // 필요 없을 때 한 번 끄는 쪽이 손이 덜 간다.
  // 평문 모드를 남겨 두는 이유: 답장 인용문·서명·대용량 링크가 평문 기준으로
  // 조립돼 있어서, 그 흐름 그대로 쓰고 싶을 때가 있다.
  const [rich, setRich] = useState<boolean>(() => localStorage.getItem("mail-rich") !== "0");
  const [richHtml, setRichHtml] = useState("");
  const richRef = useRef<RichEditorHandle | null>(null);

  // 서명 붙이기. 위치는 **맨 아래** — 인용한 원본까지 지나 가장 끝에 온다.
  //
  // 답장에 서명이 빠지던 이유:
  //   서식 편집기(기본값)는 initialHtml 을 **비어 있을 때만** 받아들인다. 새 메일은
  //   본문이 비어 있다가 이 effect 가 서명을 넣어 주므로 편집기가 그걸 받는다.
  //   그런데 답장·전달은 인용문이 처음부터 들어차 있어서 편집기가 먼저 인용문으로
  //   채워지고, 뒤늦게 body(평문)에 붙인 서명은 편집기에 영영 반영되지 않았다.
  //   평문 모드에서만 서명이 보이고 서식 모드에서는 사라지는 이유가 이것이다.
  //   그래서 서식 모드에서는 편집기에 직접 덧붙인다.
  useEffect(() => {
    if (!open) return;
    const sig = signature?.body_text?.trim();
    if (!signature?.auto_attach || !sig) return;

    // 평문 모드용 — 발송 본문과 평문↔서식 전환의 기준이 된다.
    setBody((cur) => {
      if (cur.includes(sig)) return cur;          // 이미 들어 있으면 두 번 넣지 않는다
      const block = SIG_SEPARATOR + sig;
      return cur ? `${cur.replace(/\s+$/, "")}\n\n${block}\n` : `\n\n${block}\n`;
    });

    // 서식 모드용 — 편집기가 이미 인용문으로 차 있어도 맨 끝에 붙는다.
    // 공백 차이를 무시하고 비교해야 같은 서명을 두 번 넣지 않는다.
    if (rich) {
      const ed = richRef.current;
      if (ed) {
        const flat = (t: string) => t.replace(/\s+/g, " ").trim();
        if (!flat(ed.getText()).includes(flat(sig))) {
          ed.setHtml(ed.getHtml() + signatureHtml(sig));
        }
      }
    }
    // signature 는 부모가 나중에 불러올 수 있다. 늦게 도착해도 붙도록 값 자체를 의존성에 둔다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, rich, signature?.auto_attach, signature?.body_text]);

  // 작성 중에는 테마(플로팅) 버튼을 숨긴다.
  // 둘 다 우측 하단 고정인데 테마 버튼의 z-index 가 더 높아 '보내기' 를 덮는다.
  // 메일을 쓰는 동안 테마를 바꿀 일은 없으니, 겹치는 쪽을 비켜 주는 편이 낫다.
  useEffect(() => {
    if (!open) return;
    document.body.classList.add("mail-composing");
    return () => document.body.classList.remove("mail-composing");
  }, [open]);

  // 30초마다 자동 임시저장 (2026-09-14 수정).
  // 예전엔 의존성에 to/subject/body 가 들어 있어 **글자를 칠 때마다 타이머가 처음부터 다시 돌았다** —
  // 계속 쓰는 동안엔 한 번도 저장되지 않고, 30초 동안 손을 놓아야만 저장됐다. 이제 타이머는
  // 창이 열려 있는 동안 한 번만 걸고, 회차마다 최신 persistDraft 를 ref 로 부른다.
  // 내용이 마지막 저장과 같으면 서버를 두드리지 않고, 탭을 숨길 때(다른 창으로 갈 때)도 한 번 저장한다.
  useEffect(() => {
    if (!open) return;
    const t = setInterval(() => { void persistRef.current(true); }, 30000);
    const onHide = () => { if (document.visibilityState === "hidden") void persistRef.current(true); };
    document.addEventListener("visibilitychange", onHide);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onHide); };
  }, [open]);

  if (!open) return null;

  const persistDraft = async (silent = false) => {
    if (savingRef.current) return;                 // 앞 저장이 아직 진행 중
    // 서식으로 쓴 글은 `body`(평문)에 들어오지 않는다. 편집기에서 직접 꺼내야
    // 30초마다 도는 자동저장이 빈 초안을 덮어쓰지 않는다.
    const draftText = rich ? (richRef.current?.getText() ?? "") : body;
    const draftHtml = rich ? (richRef.current?.getHtml() ?? richHtml) : undefined;
    if (!to.length && !cc.length && !bcc.length && !subject.trim() && !draftText.trim()) return;
    const attIds = atts.map((a) => a.id);
    const key = JSON.stringify([to, cc, bcc, subject, draftText, draftHtml ?? "", attIds]);
    // 자동저장은 바뀐 것이 없거나 발송·업로드 중이면 건너뛴다(손으로 누른 저장은 항상 한다).
    if (silent && (key === lastSavedKey.current || busy)) return;
    savingRef.current = true;
    try {
      if (inFlightDraftId.current) {
        await api.patch(`/mail/drafts/${inFlightDraftId.current}`, {
          to, cc, bcc, subject, body_text: draftText, body_html: draftHtml,
          attachment_ids: attIds,
        });
      } else {
        const r = await api.post<MailDetail[]>("/mail", {
          to, cc, bcc, subject, body_text: draftText, body_html: draftHtml, save_as_draft: true,
          attachment_ids: attIds,
        });
        const draft = r.data[0];
        inFlightDraftId.current = draft?.id ?? null;
      }
      lastSavedKey.current = key;
      setAutoSave({ at: new Date(), ok: true, auto: silent });
      if (!silent) toast.success("임시 저장되었습니다");
    } catch (e: any) {
      setAutoSave({ at: new Date(), ok: false, auto: silent });
      if (!silent) toast.error("저장 실패", e?.response?.data?.error?.message);
    } finally {
      savingRef.current = false;
    }
  };
  persistRef.current = persistDraft;

  // 링크는 본문에 문단으로 붙여 보낸다. 받는 쪽에 우리 계정이 없으므로,
  // 눌러서 바로 받을 수 있는 주소와 기한이 본문에 그대로 보여야 한다.
  const bodyWithLinks = (list: FileLink[] = links) => {
    if (!list.length) return body;
    const lines = list.map((l) => {
      const until = new Date(l.expires_at).toLocaleDateString("ko-KR",
        { year: "numeric", month: "long", day: "numeric" });
      return `· ${l.file_name} (${formatBytes(l.file_size)})
  ${l.url}
  ${until}까지 내려받을 수 있습니다.`;
    });
    // 서명은 맨 아래에 와야 하므로, 링크 문단은 서명 **위**에 끼워 넣는다.
    const block = `

[대용량 첨부]
${lines.join("\n")}
`;
    const si = body.lastIndexOf(SIG_SEPARATOR);
    return si < 0 ? body + block : body.slice(0, si) + block + body.slice(si);
  };

  // 첨부 → 임시 링크. 메일에서는 떼어 내고 본문 링크로 대신한다.
  const toLink = async (a: UploadedAtt) => {
    setLinking(a.id);
    try {
      const r = await api.post<FileLink>(`/mail/attachments/${a.id}/link?days=${linkDays}`, {});
      setLinks((cur) => [...cur, r.data]);
      setAtts((cur) => cur.filter((x) => x.id !== a.id));
      toast.success("대용량 링크로 바꿨습니다", `${linkDays}일간 내려받을 수 있습니다`);
    } catch (e: any) {
      toast.error("링크를 만들지 못했습니다", e?.response?.data?.error?.message ?? "");
    } finally {
      setLinking(null);
    }
  };

  const dropLink = async (l: FileLink) => {
    setLinks((cur) => cur.filter((x) => x.id !== l.id));
    try { await api.delete(`/mail/file-links/${l.id}`); } catch { /* 이미 없으면 그만 */ }
  };

  // 첨부가 크면 그냥 보내지 않고 한 번 확인한다.
  //
  // 받는 쪽 메일서버(특히 관공서·대기업)는 대개 10~25MB 에서 막는다. 그대로 보내면
  // 거부되는데, 예전에는 그 사실이 화면에 드러나지도 않아 이틀을 허비한 적이 있다.
  // 그렇다고 말없이 링크로 바꾸면 상대가 받는 모습이 달라지므로, 물어보고 정한다.
  const totalAttBytes = atts.reduce((n, a) => n + a.file_size, 0);
  const [askLinks, setAskLinks] = useState(false);

  const onSendClick = () => {
    if (!to.length) { toast.warn("받는 사람을 입력하세요"); return; }
    if (totalAttBytes > LINK_AUTO_BYTES) { setAskLinks(true); return; }
    void doSend();
  };

  /** 남은 첨부를 모두 링크로 바꾸고 곧바로 보낸다. */
  const sendAsLinks = async () => {
    setAskLinks(false);
    setBusy(true);
    const made: FileLink[] = [];
    try {
      for (const a of atts) {
        const r = await api.post<FileLink>(`/mail/attachments/${a.id}/link?days=${linkDays}`, {});
        made.push(r.data);
      }
    } catch (e: any) {
      setBusy(false);
      toast.error("링크를 만들지 못했습니다", e?.response?.data?.error?.message ?? "");
      return;
    }
    setLinks((cur) => [...cur, ...made]);
    setAtts([]);
    setBusy(false);
    // state 반영을 기다리지 않도록 방금 만든 값을 그대로 넘긴다.
    await doSend({ atts: [], links: [...links, ...made] });
  };

  /** 발송에 쓸 본문 두 벌(평문·HTML)을 만든다.
   *
   *  서식 모드에서도 **평문을 반드시 함께** 보낸다. HTML 을 못 읽는 환경에서
   *  본문이 통째로 비어 보이는 것을 막기 위해서다.
   *
   *  대용량 첨부 안내는 **본문 맨 위**에 카드로 올린다. 아래에 붙이면 긴 답장에서는
   *  한참 스크롤해야 보여서, 받는 사람이 파일이 온 줄도 모르고 넘어간다. */
  const buildBody = (useLinks: FileLink[]) => {
    if (!rich) {
      const text = bodyWithLinks(useLinks);
      return { text, html: renderMailHtml(text) };
    }
    const html = richRef.current?.getHtml() ?? richHtml;
    const text = richRef.current?.getText() ?? "";
    if (!useLinks.length) return { text, html };
    return { text: largeAttachText(useLinks) + text, html: largeAttachHtml(useLinks, linkDays) + html };
  };

  const doSend = async (over?: { atts?: UploadedAtt[]; links?: FileLink[] }) => {
    const useAtts = over?.atts ?? atts;
    const useLinks = over?.links ?? links;
    const built = buildBody(useLinks);
    if (!to.length) { toast.warn("받는 사람을 입력하세요"); return; }
    setBusy(true);
    try {
      const payload: any = {
        to, cc, bcc, subject,
        body_text: built.text,
        // 평문만 보내면 받는 쪽에서 서명·인용문이 본문과 뒤섞여 지저분하다.
        // 같은 내용을 HTML 로도 함께 보내 서명은 흐린 글씨, 원본은 세로줄로 보이게 한다.
        // (평문도 같이 보내므로 HTML 을 못 읽는 곳에서도 그대로 읽힌다)
        body_html: built.html,
        save_as_draft: false,
        individual,
        in_reply_to_id: initial?.in_reply_to_id ?? null,
        attachment_ids: useAtts.map((a) => a.id),
      };
      if (scheduledAt) payload.scheduled_at = new Date(scheduledAt).toISOString();
      let sentRows: any[] = [];

      // If editing an existing draft, send via /drafts/:id/send first to drop the
      // staging row.  For fresh composes we just POST.
      if (inFlightDraftId.current) {
        // 임시저장을 거쳐 보내는 경로도 HTML 을 같이 저장해야 한다 —
        // 여기서 빠뜨리면 초안으로 쓴 메일만 평문으로 나가 서식이 달라진다.
        await api.patch(`/mail/drafts/${inFlightDraftId.current}`, {
          to, cc, bcc, subject, body_text: built.text,
          body_html: built.html,
          attachment_ids: useAtts.map((a) => a.id),
          // 예약·개별 발송도 초안에 실어야 한다 — 빠뜨리면 예약 메일이 바로 나가고(2026-10-23)
          // 개별 발송이 일반 발송으로 바뀐다. 예약을 지웠으면 null 로 확실히 지운다.
          scheduled_at: scheduledAt ? new Date(scheduledAt).toISOString() : null,
          individual,
        }, LONG_REQ);
        // 첨부가 크면 서버가 파일을 읽어 SMTP 로 밀어 넣는 데 30초를 훌쩍 넘긴다.
        // 기본 제한(30초)이면 브라우저만 먼저 끊겨 '실패' 로 보이는데 서버는 계속 보내고
        // 있어서, 다시 누르면 초안이 이미 소비된 뒤라 멈춘 것처럼 보였다(2026-09-23).
        const r1 = await api.post(`/mail/drafts/${inFlightDraftId.current}/send`, {}, LONG_REQ);
        sentRows = Array.isArray(r1.data) ? r1.data : [];
        inFlightDraftId.current = null;   // 이 초안은 소비됐다 - 다시 누르면 새로 만든다
      } else {
        const r2 = await api.post("/mail", payload, LONG_REQ);
        sentRows = Array.isArray(r2.data) ? r2.data : [];
      }
      // 서버는 사내 사본을 남긴 뒤 외부 발송에 실패해도 200 을 돌려준다(사본까지 날리지 않으려고).
      // 여기서 말해 주지 않으면 화면에는 '발송 완료' 로 보여, 상대가 못 받은 것을 한참 뒤에 안다.
      const failedRow = sentRows.find((m) => m && m.delivery_error);
      if (failedRow) {
        toast.error("보내지 못했습니다", String(failedRow.delivery_error).slice(0, 160));
      } else {
        toast.success(scheduledAt ? "예약 발송 등록" : "발송 완료");
      }
      // 보낸 뒤 어디로 갈지는 **부모가** 정한다(작성 페이지는 전체 메일함으로 보낸다).
      // 여기서 onClose() 까지 부르면 그 이동을 history 뒤로가기가 곧바로 덮어써서
      // 원래 있던 화면으로 되돌아간다 — 이동이 안 되는 것처럼 보이던 이유다.
      onSent();
    } catch (e: any) {
      // 초안 경로에서 404 면 그 초안은 이미 보내졌거나 사라진 것이다. 번호를 쥐고 있으면
      // 다시 눌러도 같은 404 만 반복한다 - 버리고 새로 만들게 한다.
      if (e?.response?.status === 404) inFlightDraftId.current = null;
      toast.error("발송 실패", e?.response?.data?.error?.message ?? e?.message ?? "");
    } finally {
      setBusy(false);
    }
  };

  const imageInput = useRef<HTMLInputElement>(null);

  /** 본문에 넣을 이미지. 첨부 목록에 쌓지 않고 글 안에 바로 보이게 한다.
   *
   *  data URL 로 박으면 메일 용량이 폭증하고 상당수 메일 앱이 아예 안 보여 준다.
   *  그래서 서버에 올린 뒤 **대용량 링크와 같은 공개 주소**로 넣는다.
   *  받는 사람이 로그인 없이 볼 수 있어야 이미지가 뜬다. */
  const onPickInlineImages = async (files: FileList | File[] | null) => {
    if (!files || !files.length) return;
    setBusy(true);
    for (const raw of Array.from(files)) {
      if (!isImageFile(raw)) continue;
      const f = asImageFile(raw);          // 종류가 빈 파일은 확장자로 채워야 서버가 그림으로 저장한다
      setUploading({ name: f.name, pct: 0 });
      try {
        const fd = new FormData();
        fd.append("file", f);
        const up = await api.post<UploadedAtt>("/mail/attachments/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 0,
          onUploadProgress: (ev) => {
            if (ev.total) setUploading({ name: f.name, pct: Math.round((ev.loaded / ev.total) * 100) });
          },
        });
        // 본문 이미지는 오래 살아 있어야 한다(메일은 나중에 다시 열어 본다).
        const link = await api.post<FileLink>(`/mail/attachments/${up.data.id}/link?days=30`, {});
        richRef.current?.insertHtml(
          // 링크 주소(`/mail/dl/<토큰>`)는 **안내 페이지(HTML)** 다. 그림 태그에는 파일 자체를
          // 주는 `/file` 을 써야 한다 - 안내 페이지를 src 로 넣으면 받는 쪽에서도, 쓰는 화면에서도
          // 깨진 그림에 파일 이름만 보인다(2026-09-30 확인).
          `<img src="${link.data.url}/file?inline=1" alt="${f.name}" style="max-width:100%;height:auto"><br>`,
        );
      } catch (e: any) {
        toast.error("이미지 넣기 실패", e?.response?.data?.error?.message || f.name);
      } finally {
        setUploading(null);
      }
    }
    setBusy(false);
    if (imageInput.current) imageInput.current.value = "";
  };

  const onPickFiles = async (files: FileList | File[] | null) => {
    if (!files || !files.length) return;
    setBusy(true);
    for (const f of Array.from(files)) {
      if (f.size > MAX_ATTACH_BYTES) {
        // 올리고 나서 거절당하면 큰 파일일수록 기다린 시간이 아깝다 — 보내기 전에 막는다.
        toast.error("첨부 실패", `${f.name} — 최대 5GB까지 첨부할 수 있습니다 (${formatBytes(f.size)})`);
        continue;
      }
      setUploading({ name: f.name, pct: 0 });
      try {
        // 8MB 조각으로 나눠 올린다 — 나스 프록시가 요청 하나를 2GB 에서 끊고,
        // 중간에 끊겨도 받은 곳부터 이어 올린다(같은 파일을 다시 고르면 이어서).
        const att = await chunkedUpload<UploadedAtt>(fileSource(f), {
          base: "/mail/attachments/uploads",
          onProgress: (p) => setUploading({ name: f.name, pct: p.total ? Math.floor((p.loaded / p.total) * 100) : 100 }),
        });
        setAtts((cur) => [...cur, att]);
      } catch (e: any) {
        toast.error("첨부 실패", e?.response?.data?.error?.message || f.name);
      } finally {
        setUploading(null);
      }
    }
    setBusy(false);
  };

  const removeAtt = (id: number) => setAtts((cur) => cur.filter((a) => a.id !== id));

  // 드래그로 첨부 — 창 어디에 떨어뜨려도 받는다. dragenter/leave 가 자식 위를 지날 때마다
  // 오가므로 깊이를 세어 0 이 될 때만 표시를 끈다(안 그러면 테두리가 깜빡인다).
  const dragDepth = useRef(0);
  const onDragEnter = (e: React.DragEvent) => {
    if (!Array.from(e.dataTransfer.types || []).includes("Files")) return;
    e.preventDefault();
    dragDepth.current++;
    setDragOver(true);
  };
  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragOver(false);
  };
  const onDrop = (e: React.DragEvent) => {
    if (!Array.from(e.dataTransfer.types || []).includes("Files")) return;
    e.preventDefault();
    dragDepth.current = 0;
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files || []);
    // 본문(서식 편집기) 안에 떨어뜨린 그림은 첨부가 아니라 **그 자리에 그림으로** 넣는다(2026-09-14 요청).
    // 그림이 아닌 파일은 어디에 떨어뜨리든 첨부다.
    const rich = richRef.current;
    if (rich && rich.containsNode(e.target as Node)) {
      const imgs = files.filter(isImageFile);
      const others = files.filter((f) => !isImageFile(f));
      if (imgs.length) { rich.placeCaretAt(e.clientX, e.clientY); void onPickInlineImages(imgs); }
      if (others.length) void onPickFiles(others);
      return;
    }
    void onPickFiles(files);
  };

  // 주소 칩을 다른 칸(받는사람 ↔ 참조 ↔ 숨은참조)으로 끌어 옮긴다.
  // 답장할 때 받는사람에 들어온 주소를 참조로 내리는 일이 잦은데, 지우고 다시 치는 것보다
  // 칩을 그대로 끌어다 놓는 쪽이 빠르고 오타가 없다(2026-09-14 요청).
  const boxes: Record<RecipientBox, { list: string[]; set: (v: string[]) => void; label: string }> = {
    to: { list: to, set: setTo, label: "받는사람" },
    cc: { list: cc, set: setCc, label: "참조" },
    bcc: { list: bcc, set: setBcc, label: "숨은참조" },
  };
  const moveChip = (email: string, from: RecipientBox, dest: RecipientBox) => {
    if (from === dest) return;
    const src = boxes[from], dst = boxes[dest];
    if (!src.list.includes(email)) return;
    if (!dst.list.includes(email)) {
      if (dst.list.length >= 10) { toast.warn(`${dst.label}은 최대 10개까지입니다`); return; }
      dst.set([...dst.list, email]);
    }
    src.set(src.list.filter((x) => x !== email));
  };

  return (
    <div className={`mail-compose ${asPage ? "as-page" : ""}`} role="dialog" aria-label="메일 작성"
      onDragEnter={onDragEnter} onDragLeave={onDragLeave}
      onDragOver={(e) => { if (Array.from(e.dataTransfer.types || []).includes("Files")) e.preventDefault(); }}
      onDrop={onDrop}
      style={{ position: "relative" }}>
      {dragOver && (
        <div style={{
          position: "absolute", inset: 0, zIndex: 30, pointerEvents: "none",
          background: "rgba(99,102,241,0.08)", border: "2px dashed var(--p-500,#6366f1)", borderRadius: 10,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <div style={{ background: "var(--bg-0,#fff)", padding: "12px 20px", borderRadius: 10, boxShadow: "0 8px 28px rgba(0,0,0,.16)", textAlign: "center" }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "var(--p-600,#4f46e5)" }}>여기에 놓으면 첨부됩니다</div>
            <div style={{ fontSize: 11.5, color: "var(--text-3)", marginTop: 3 }}>파일당 최대 5GB</div>
          </div>
        </div>
      )}
      {uploading && (
        <div style={{ position: "absolute", left: 0, right: 0, top: 0, zIndex: 31 }}>
          <div style={{ height: 3, background: "var(--border)" }}>
            <div style={{ height: "100%", width: `${uploading.pct}%`, background: "var(--p-500,#6366f1)", transition: "width .2s" }} />
          </div>
          <div style={{ fontSize: 11, color: "var(--text-3)", padding: "2px 10px", background: "var(--bg-0)" }}>
            첨부 올리는 중… {uploading.name} ({uploading.pct}%)
          </div>
        </div>
      )}
      <div className="ch">
        {asPage && (
          <button className="t-iconbtn" onClick={onClose} title="뒤로"><I.ArrowR size={18} style={{ transform: "scaleX(-1)" }}/></button>
        )}
        <h3>{initial?.in_reply_to_id ? "답장" : draftId ? (initial?.scheduled_at ? "예약 메일 수정" : "임시 메일 편집") : "새 메일 작성"}</h3>
        <button className="t-iconbtn" onClick={() => void persistDraft()} title="임시 저장"><I.Paper size={16}/></button>
        {!asPage && <button className="t-iconbtn" onClick={onClose} title="닫기"><I.X size={16}/></button>}
      </div>
      <div className="cb">
        {/* 받는사람만 대량 업로드를 붙인다 — 명단으로 보내는 것은 보통 '받는사람'이다.
            손으로 넣는 개수는 10개로 제한하고, 그 이상은 엑셀로 올리게 한다. */}
        <ChipField label="받는사람" value={to} onChange={setTo} max={10} onBulk boxId="to" onMoveIn={moveChip}/>
        <ChipField label="참조" value={cc} onChange={setCc} max={10} boxId="cc" onMoveIn={moveChip}/>
        <ChipField label="숨은참조" value={bcc} onChange={setBcc} max={10} boxId="bcc" onMoveIn={moveChip}/>
        <div className="field" style={{ gap: 10 }}>
          <span className="key">옵션</span>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--text-2)", cursor: "pointer" }}>
            <input type="checkbox" checked={individual} onChange={(e) => setIndividual(e.target.checked)}/>
            개별 발송 <span style={{ color: "var(--text-3)", fontSize: 12 }}>(수신자끼리 서로의 주소가 보이지 않음)</span>
          </label>
        </div>
        {atts.length > 0 && (
          <div className="att-row top">
            {atts.map((a) => (
              <span key={a.id} className="att-item">
                <I.Paper size={12}/> {a.file_name}
                <span style={{ color: "var(--text-3)" }}>({formatBytes(a.file_size)})</span>
                {/* 큰 파일만 링크 전환을 권한다 — 작은 첨부까지 링크로 만들면 오히려 불편하다. */}
                {a.file_size >= LINK_SUGGEST_BYTES && (
                  <button onClick={() => void toLink(a)} disabled={linking === a.id}
                    title="메일에 싣지 않고 내려받기 주소로 보냅니다"
                    style={{ marginLeft: 4, background: "transparent", border: "none",
                             color: "var(--p-600)", cursor: "pointer", fontSize: 11, textDecoration: "underline" }}>
                    {linking === a.id ? "만드는 중…" : "🔗 링크로"}
                  </button>
                )}
                <button onClick={() => removeAtt(a.id)} aria-label="첨부 삭제"><I.X size={12}/></button>
              </span>
            ))}
          </div>
        )}

        {/* 총 용량이 크면 그냥 보내면 거부당한다. 보내기 전에 알려 주는 편이 낫다. */}
        {atts.reduce((n, a) => n + a.file_size, 0) >= LINK_SUGGEST_BYTES && (
          <div style={{ margin: "2px 0 0", padding: "8px 10px", borderRadius: 8, fontSize: 12,
                        border: "1px solid var(--warn, #f59e0b)", background: "rgba(245,158,11,.08)",
                        color: "var(--text-2)", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span>⚠️ 첨부가 큽니다. 받는 쪽 메일서버(특히 관공서)가 거부할 수 있으니 <b>링크로</b> 보내는 편이 안전합니다.</span>
            <label style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 4 }}>
              유효기간
              <select value={linkDays} onChange={(e) => setLinkDays(Number(e.target.value))}
                      style={{ fontSize: 12, padding: "2px 4px" }}>
                <option value={3}>3일</option>
                <option value={7}>7일</option>
                <option value={30}>30일</option>
              </select>
            </label>
          </div>
        )}

        {links.length > 0 && (
          <div className="att-row top" style={{ flexDirection: "column", alignItems: "stretch", gap: 6 }}>
            {links.map((l) => (
              <div key={l.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12,
                                       padding: "6px 10px", borderRadius: 8,
                                       border: "1px solid var(--border)", background: "var(--bg-0)" }}>
                <span>🔗</span>
                <b style={{ fontSize: 12.5 }}>{l.file_name}</b>
                <span style={{ color: "var(--text-3)" }}>({formatBytes(l.file_size)})</span>
                <span style={{ color: "var(--text-3)", marginLeft: "auto" }}>
                  {new Date(l.expires_at).toLocaleDateString("ko-KR", { month: "long", day: "numeric" })}까지
                </span>
                <button onClick={() => void dropLink(l)} aria-label="링크 삭제"
                        style={{ background: "transparent", border: "none", color: "var(--text-3)", cursor: "pointer" }}>
                  <I.X size={12}/>
                </button>
              </div>
            ))}
            <span style={{ fontSize: 11.5, color: "var(--text-3)" }}>
              발송할 때 본문 아래에 내려받기 주소가 함께 들어갑니다. 받는 분은 로그인 없이 받을 수 있습니다.
            </span>
          </div>
        )}
        <div className="field">
          <span className="key">제목</span>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="(제목 없음)"/>
        </div>
        {/* 서식/평문 전환. 평문을 남겨 둔 이유는 답장 인용문·서명이 평문 기준이라
            서식을 강제하면 그 흐름이 흔들리기 때문이다. 선택은 기억해 둔다. */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 6, padding: "2px 0" }}>
          <button type="button"
            onClick={() => {
              const next = !rich;
              // 평문 → 서식으로 갈 때 지금까지 쓴 글을 잃지 않도록 옮겨 담는다.
              if (next) setRichHtml(renderMailHtml(body));
              else if (richRef.current) setBody(richRef.current.getText());
              setRich(next);
              localStorage.setItem("mail-rich", next ? "1" : "0");
            }}
            style={{ fontSize: 11.5, padding: "3px 9px", borderRadius: 6, cursor: "pointer",
                     border: "1px solid var(--border)",
                     background: rich ? "var(--p-tint)" : "transparent",
                     color: rich ? "var(--p-600)" : "var(--text-3)" }}>
            {rich ? "✎ 서식 편집 중 — 일반 텍스트로" : "✎ 서식 편집기 사용"}
          </button>
        </div>

        {rich ? (
          <RichEditor
            key={String(rich)}
            // 서식이 기본이 되면서 답장 인용문·서명이 평문(body)으로 먼저 채워진다.
            // 그대로 두면 편집기가 빈 채로 열려 그 내용을 잃는다 — 같은 내용을
            // HTML 로 바꿔 넘긴다. 편집기는 **비어 있을 때만** 받아들이므로
            // (서명은 창이 열린 뒤에 붙는다) 늦게 온 내용도 놓치지 않는다.
            // 초안·다시보내기는 저장해 둔 HTML 을 그대로 되살린다. 평문에서
            // 다시 만들면 표·색·이미지가 전부 사라진다(저장은 되는데 열면 없어졌다).
            initialHtml={richHtml || initial?.body_html || (body.trim() ? renderMailHtml(body) : "")}
            editorRef={richRef}
            onChange={setRichHtml}
            onRequestImage={() => imageInput.current?.click()}
            onPasteImages={(files) => void onPickInlineImages(files)}
            minHeight={asPage ? 300 : 200}
          />
        ) : (
          <textarea className="body-input" placeholder="메일 본문을 입력하세요" value={body}
            onChange={(e) => setBody(e.target.value)}
            // 평문 모드에는 이미지를 그려 넣을 자리가 없다. 그렇다고 조용히 버리면
            // 붙여넣은 사람은 사라진 줄도 모른다 — 첨부로 받아 두고 알려 준다.
            onPaste={(e) => {
              const imgs = Array.from(e.clipboardData.files || []).filter(isImageFile).map(asImageFile);
              if (!imgs.length) return;
              e.preventDefault();
              void onPickFiles(imgs);
              toast.info("이미지를 첨부했습니다", "본문 안에 넣으려면 ‘✎ 서식 편집기 사용’을 켜고 붙여넣으세요.");
            }}/>
        )}

        {/* 본문에 넣을 이미지. 첨부와 달리 본문 안에 바로 보이게 한다. */}
        <input ref={imageInput} type="file" accept="image/*" multiple style={{ display: "none" }}
               onChange={(e) => void onPickInlineImages(e.target.files)}/>
      </div>
      <div className="cf">
        <div className="left">
          <input ref={fileInput} type="file" multiple style={{ display: "none" }}
                 onChange={(e) => onPickFiles(e.target.files)}/>
          <button className="t-btn t-btn-sm t-btn-outline" onClick={() => fileInput.current?.click()} disabled={busy}>
            <I.Paper size={12}/> 첨부
          </button>
          <button className="t-btn t-btn-sm t-btn-outline" onClick={() => setShowSchedule(!showSchedule)}>
            <I.Clock size={12}/> {scheduledAt ? new Date(scheduledAt).toLocaleString("ko-KR", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "예약"}
          </button>
          <button className="t-btn t-btn-sm t-btn-outline"
            onClick={() => { setAiPreset(undefined); setAiOpen(true); }}
            disabled={!(rich ? (richHtml.trim() || body.trim()) : body.trim())} title="AI로 본문 첨삭">
            <I.Sparkles size={12}/> AI 첨삭
          </button>
          {/* 오탈자 검수는 가장 자주 쓰는 첨삭이라 한 번에 열리게 따로 뺐다. */}
          <button className="t-btn t-btn-sm t-btn-outline"
            onClick={() => { setAiPreset("맞춤법과 오탈자만 고쳐 주세요. 문장의 뜻과 말투는 그대로 두세요."); setAiOpen(true); }}
            disabled={!(rich ? (richHtml.trim() || body.trim()) : body.trim())} title="맞춤법·오탈자 검수">
            ㄱ/ㄴ 맞춤법
          </button>
          {showSchedule && (
            <div className="sched-pop">
              <input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)}/>
              <button className="t-btn t-btn-sm" onClick={() => { setScheduledAt(""); setShowSchedule(false); }} style={{ marginLeft: 6 }}>지우기</button>
              <button className="t-btn t-btn-sm t-btn-primary" onClick={() => setShowSchedule(false)} style={{ marginLeft: 6 }}>확인</button>
            </div>
          )}
        </div>
        <div className="right">
          <button className="t-btn t-btn-sm" onClick={() => void persistDraft()} disabled={busy}>임시저장</button>
          {autoSave && (
            <span title="30초마다 바뀐 내용이 있으면 자동으로 임시 저장됩니다"
              style={{ fontSize: 11, color: autoSave.ok ? "var(--text-3)" : "#dc2626", whiteSpace: "nowrap" }}>
              {autoSave.ok ? (autoSave.auto ? "자동 저장 " : "저장됨 ") : "자동 저장 실패 "}
              {autoSave.at.toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
            </span>
          )}
          <button className="t-btn t-btn-sm t-btn-primary" onClick={onSendClick} disabled={busy}>
            <I.Send size={12}/> 발송
          </button>
        </div>
      </div>

      {aiOpen && (
        <AiReviseDialog
          // 서식 모드에서는 태그를 빼고 글만 넘긴다. HTML 을 통째로 보내면
          // AI 가 마크업을 건드려 서식이 깨진다.
          // 답장·전달의 인용문(이전 메일)과 서명은 첨삭 대상에서 뺀다 — 내가 쓴
          // 글만 넘기고, 적용할 때 인용문·서명을 원문 그대로 다시 붙인다.
          original={splitMailText(rich ? (richRef.current?.getText() ?? "") : body).mine.trim()}
          protectedNote={(() => {
            const { quote, sig } = splitMailText(rich ? (richRef.current?.getText() ?? "") : body);
            return Boolean(quote.trim() || sig.trim());
          })()}
          preset={aiPreset}
          onClose={() => { setAiOpen(false); setAiPreset(undefined); }}
          onApply={(revised) => {
            // 인용문·서명은 적용 시점의 편집기 내용에서 다시 떼어 그대로 잇는다.
            const { quote, sig } = splitMailText(rich ? (richRef.current?.getText() ?? "") : body);
            let out = revised.trimEnd();
            if (quote.trim()) out += `\n\n${quote.trimEnd()}\n`;
            if (sig) out += sig;
            if (rich) {
              // 편집기에 **직접** 써 넣어야 한다. 상태(richHtml)만 바꾸면 화면은
              // 그대로여서, 첨삭한 줄 알고 보냈는데 고치기 전 글이 나간다.
              // (initialHtml 은 비어 있을 때만 반영되므로 이 경로로는 못 바꾼다)
              const html = renderMailHtml(out);
              setRichHtml(html);
              richRef.current?.setHtml(html);
            } else {
              setBody(out);
            }
            setAiOpen(false); setAiPreset(undefined);
            toast.success("AI 첨삭 내용을 적용했습니다");
          }}
        />
      )}

      {/* 큰 첨부를 그대로 보낼지, 링크로 바꿔 보낼지 마지막에 한 번 묻는다. */}
      {askLinks && (
        <div onClick={() => setAskLinks(false)}
          style={{ position: "fixed", inset: 0, zIndex: 210, background: "rgba(0,0,0,.5)",
                   display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: "var(--surface,#fff)", borderRadius: 12, width: "min(520px, 94vw)",
                     padding: "20px 22px", boxShadow: "0 20px 60px rgba(0,0,0,.35)" }}>
            <h3 style={{ margin: "0 0 10px", fontSize: 15.5 }}>첨부가 큽니다 — 어떻게 보낼까요?</h3>
            <p style={{ margin: "0 0 6px", fontSize: 13, lineHeight: 1.7, color: "var(--text-2)" }}>
              첨부 {atts.length}개, 모두 합쳐 <b>{formatBytes(totalAttBytes)}</b> 입니다.
              메일에 실리면 <b>약 {formatBytes(Math.round(totalAttBytes * 1.37))}</b> 로 부풀고(base64),
              네이버·관공서 같은 곳은 보통 10~20MB 에서 거부합니다. 거부는 한참 뒤 반송메일로
              돌아오기 때문에 보낸메일함에는 정상으로 보입니다.
            </p>
            <p style={{ margin: "0 0 14px", fontSize: 12.5, lineHeight: 1.7, color: "var(--text-3)" }}>
              링크로 보내면 파일은 우리 서버에 두고 본문에 내려받기 주소가 들어갑니다.
              받는 분은 로그인 없이 받을 수 있고, 기한이 지나면 닫힙니다.
            </p>

            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5,
                            marginBottom: 16, color: "var(--text-2)" }}>
              링크 유효기간
              <select value={linkDays} onChange={(e) => setLinkDays(Number(e.target.value))}
                      style={{ fontSize: 12.5, padding: "3px 6px" }}>
                <option value={3}>3일</option>
                <option value={7}>7일</option>
                <option value={30}>30일</option>
              </select>
            </label>

            {totalAttBytes > SMTP_HARD_LIMIT_BYTES && (
              <p style={{ fontSize: 12.5, color: "var(--danger,#ef4444)", margin: "0 0 10px", lineHeight: 1.6 }}>
                지금 첨부는 {formatBytes(totalAttBytes)} 입니다. 메일 규격상 {formatBytes(SMTP_HARD_LIMIT_BYTES)} 를
                넘으면 받는 쪽 서버가 거부하므로 <b>링크로만</b> 보낼 수 있습니다.
              </p>
            )}

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button className="t-btn t-btn-sm" onClick={() => setAskLinks(false)}>취소</button>
              <button className="t-btn t-btn-sm" disabled={busy || totalAttBytes > SMTP_HARD_LIMIT_BYTES}
                title={totalAttBytes > SMTP_HARD_LIMIT_BYTES
                  ? `메일에 실을 수 있는 한계(${formatBytes(SMTP_HARD_LIMIT_BYTES)})를 넘었습니다 - 링크로만 보낼 수 있습니다`
                  : undefined}
                onClick={() => { setAskLinks(false); void doSend(); }}>
                그대로 첨부로 보내기
              </button>
              <button className="t-btn t-btn-sm t-btn-primary" disabled={busy}
                onClick={() => void sendAsLinks()}>
                🔗 링크로 바꿔 보내기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── AI 첨삭: 명령어로 본문을 다듬고, 원본과 비교 후 적용 ─────────────────────────
function AiReviseDialog({ original, onClose, onApply, preset, protectedNote }:
  { original: string; onClose: () => void; onApply: (revised: string) => void; preset?: string;
    /** 인용문·서명이 첨삭에서 제외돼 있음을 알리는 안내 표시 여부 */
    protectedNote?: boolean }) {
  // preset 이 있으면 지시문을 미리 채워 둔다(맞춤법 검사 버튼으로 열었을 때).
  const [instruction, setInstruction] = useState(preset ?? "");
  const [busy, setBusy] = useState(false);
  const [revised, setRevised] = useState<string | null>(null);
  const [modelName, setModelName] = useState<string | null>(null);

  const PRESETS = [
    "더 정중하고 격식 있게",
    "간결하게 요약",
    "오타·맞춤법 교정",
    "비즈니스 이메일 톤으로",
    "영어로 번역",
  ];

  const run = async (instr: string) => {
    const text = (instr || "").trim();
    if (!text) { toast.warn("첨삭 명령을 입력하세요"); return; }
    if (!original.trim()) { toast.warn("본문이 비어 있습니다"); return; }
    setBusy(true);
    setRevised(null);
    try {
      const prompt =
        `다음 이메일 본문을 아래 지시에 따라 첨삭/수정해 주세요.\n` +
        `결과로 수정된 본문만 출력하고, 설명·머리말·따옴표는 넣지 마세요.\n\n` +
        `[지시] ${text}\n\n` +
        `=== 본문 시작 ===\n${original}\n=== 본문 끝 ===`;
      const r = await api.post<{ content: string; model_name: string }>("/ai/chat", {
        messages: [{ role: "user", content: prompt }],
      });
      setRevised((r.data.content || "").trim());
      setModelName(r.data.model_name);
    } catch (e: any) {
      const msg = e?.response?.data?.detail ?? e?.response?.data?.error?.message ?? "AI 호출에 실패했습니다";
      toast.error("AI 첨삭 실패", msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="t-overlay" onClick={onClose}>
      <div className="t-cmdk" onClick={(e) => e.stopPropagation()} style={{ width: 820, maxWidth: "94vw", maxHeight: "86vh", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 8 }}>
          <I.Sparkles size={16}/>
          <h3 style={{ margin: 0, flex: 1, fontSize: 15, fontWeight: 700 }}>AI 첨삭</h3>
          {modelName && <span style={{ fontSize: 11, color: "var(--text-3)" }}>{modelName}</span>}
          <button className="t-iconbtn" onClick={onClose}><I.X size={16}/></button>
        </div>

        <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10, overflow: "auto" }}>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              className="t-input"
              style={{ flex: 1 }}
              placeholder="첨삭 명령 입력 (예: 더 정중하게, 오타 수정, 영어로 번역)"
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") run(instruction); }}
              autoFocus
            />
            <button className="t-btn t-btn-primary" onClick={() => run(instruction)} disabled={busy}>
              {busy ? "첨삭 중…" : "첨삭 실행"}
            </button>
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {PRESETS.map((p) => (
              <button key={p} className="t-btn t-btn-sm t-btn-outline" disabled={busy}
                onClick={() => { setInstruction(p); run(p); }}>{p}</button>
            ))}
          </div>

          {protectedNote && (
            <div style={{ fontSize: 12, color: "var(--text-3)", background: "var(--panel)", border: "1px solid var(--border)", borderRadius: 8, padding: "6px 10px" }}>
              인용된 이전 메일과 서명은 첨삭 대상에서 제외되며, 적용 후에도 그대로 유지됩니다.
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 4 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-3)", marginBottom: 6 }}>원본(내가 쓴 글)</div>
              <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: "inherit", fontSize: 13, lineHeight: 1.6, background: "var(--panel)", border: "1px solid var(--border)", borderRadius: 8, padding: 12, margin: 0, minHeight: 220, maxHeight: 360, overflow: "auto" }}>{original}</pre>
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--p-600,#4f46e5)", marginBottom: 6 }}>AI 첨삭 결과</div>
              <textarea
                style={{ width: "100%", fontFamily: "inherit", fontSize: 13, lineHeight: 1.6, border: "1px solid var(--p-400,#818cf8)", borderRadius: 8, padding: 12, minHeight: 220, maxHeight: 360, resize: "vertical", background: "var(--surface)", color: "var(--text)" }}
                placeholder={busy ? "생성 중…" : "명령을 입력하고 첨삭을 실행하세요. 결과는 여기서 직접 수정할 수도 있습니다."}
                value={revised ?? ""}
                onChange={(e) => setRevised(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div style={{ padding: "12px 16px", borderTop: "1px solid var(--border)", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button className="t-btn" onClick={onClose}>취소</button>
          <button className="t-btn t-btn-primary" disabled={!revised || busy} onClick={() => revised && onApply(revised)}>
            본문에 적용
          </button>
        </div>
      </div>
    </div>
  );
}

/** 엑셀·CSV 명단에서 주소만 뽑아 수신자에 넣는다. 파싱은 서버가 한다
 *  (열 구성이 제각각인 실제 명단을 다루려면 전체 셀을 훑어야 해서). */
function BulkUpload({ onLoaded }: { onLoaded: (emails: string[]) => void }) {
  const ref = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const pick = async (f: File | undefined) => {
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post<{ emails: string[]; total_found: number; truncated: boolean }>(
        "/mail/recipients/import", fd, { timeout: 0 },
      );
      onLoaded(r.data.emails);
      toast.success(`${r.data.emails.length}명을 불러왔습니다`,
        r.data.truncated ? "너무 많아 일부만 가져왔습니다" : undefined);
    } catch (e: any) {
      toast.error("불러오기 실패", e?.response?.data?.error?.message ?? e?.response?.data?.detail ?? "");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";   // 같은 파일 다시 고를 수 있게
    }
  };

  return (
    <>
      <input ref={ref} type="file" accept=".xlsx,.xlsm,.csv,.txt" style={{ display: "none" }}
        onChange={(e) => pick(e.target.files?.[0])} />
      <button type="button" onClick={() => ref.current?.click()} disabled={busy}
        title="엑셀(.xlsx)·CSV 명단에서 주소를 한 번에 불러옵니다"
        style={{ fontSize: 11, padding: "2px 9px", borderRadius: 6, border: "1px solid var(--border)",
                 background: "var(--bg-0)", color: "var(--text-2)", cursor: busy ? "wait" : "pointer", whiteSpace: "nowrap" }}>
        {busy ? "불러오는 중…" : "📄 엑셀"}
      </button>
    </>
  );
}

function ChipField({ label, value, onChange, extra, max, onBulk, boxId, onMoveIn }: {
  label: string; value: string[]; onChange: (v: string[]) => void;
  extra?: React.ReactNode;
  max?: number;                 // 손으로 넣을 수 있는 최대 개수
  onBulk?: boolean;             // 엑셀/CSV 대량 업로드 버튼을 붙일지
  boxId?: RecipientBox;         // 칩을 끌어 옮길 때 이 칸을 가리키는 이름
  onMoveIn?: (email: string, from: RecipientBox, dest: RecipientBox) => void;
}) {
  const [input, setInput] = useState("");
  // 다른 칸의 칩이 이 칸 위에 떠 있는 동안 표시(놓을 자리임을 알린다)
  const [chipOver, setChipOver] = useState(false);
  const hasChip = (e: React.DragEvent) => Array.from(e.dataTransfer.types || []).includes(CHIP_DND_TYPE);
  const onChipDragOver = (e: React.DragEvent) => {
    if (!boxId || !hasChip(e)) return;
    e.preventDefault();                       // 기본은 '놓기 금지'라 허용을 알려야 drop 이 온다
    e.stopPropagation();                      // 바깥의 파일 첨부 드롭 처리로 번지지 않게
    e.dataTransfer.dropEffect = "move";
    if (!chipOver) setChipOver(true);
  };
  const onChipDragLeave = (e: React.DragEvent) => {
    // 칸 안의 자식(칩·입력칸) 사이를 지날 때도 leave 가 오므로, 칸 밖으로 나갔을 때만 끈다
    const next = e.relatedTarget as Node | null;
    if (next && fieldRef.current?.contains(next)) return;
    setChipOver(false);
  };
  const onChipDrop = (e: React.DragEvent) => {
    if (!boxId || !hasChip(e)) return;
    e.preventDefault();
    e.stopPropagation();
    setChipOver(false);
    try {
      const d = JSON.parse(e.dataTransfer.getData(CHIP_DND_TYPE)) as { email: string; from: RecipientBox };
      if (d?.email && d.from && d.from !== boxId) onMoveIn?.(d.email, d.from, boxId);
    } catch { /* 형식이 다른 드래그는 무시 */ }
  };
  const [popOpen, setPopOpen] = useState(false);
  const [results, setResults] = useState<Recipient[]>([]);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const fieldRef = useRef<HTMLDivElement>(null);
  const lastQuery = useRef<string | null>(null);
  // 자동완성은 **지금 입력 중인 칸 바로 아래**에 떠야 한다.
  // 예전에는 top/left 를 36/60 으로 박아 뒀는데, 주소를 여러 개 넣으면 칩이 줄바꿈되며
  // 입력칸이 아래로 밀리는데도 팝업은 첫 줄 자리에 그대로 남아 엉뚱한 곳에 떴다.
  const [popPos, setPopPos] = useState<{ top: number; left: number }>({ top: 36, left: 60 });

  useLayoutEffect(() => {
    if (!popOpen) return;
    const el = inputRef.current, box = fieldRef.current;
    if (!el || !box) return;
    const er = el.getBoundingClientRect(), br = box.getBoundingClientRect();
    // 오른쪽으로 넘치면 안쪽으로 당긴다(220px 는 .recip-pop 의 min-width).
    const left = Math.max(0, Math.min(er.left - br.left, br.width - 220));
    setPopPos({ top: er.bottom - br.top + 4, left });
    // 칩 개수·입력 글자·결과 수가 바뀌면 입력칸이 움직이므로 다시 잰다.
  }, [popOpen, value.length, input, results.length]);

  // 입력이 비어 있어도(칸을 누르기만 해도) 조직 구성원 + 주소록을 통째로 보여 준다.
  // 사내 주소는 주소록에 자동 등록하지 않으므로, 여기서 다 보이지 않으면 동료 주소를
  // 외울 수밖에 없다. 글자를 치면 그때부터 좁혀 나가는 방식.
  useEffect(() => {
    if (!popOpen && !input.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const typed = input.trim();
        if (lastQuery.current === typed) { setPopOpen(true); return; }
        lastQuery.current = typed;
        const q = encodeURIComponent(typed);
        const size = typed ? 8 : 100;      // 빈 칸이면 전체 목록
        const [ur, cr] = await Promise.all([
          api.get<{ data: Recipient[] }>(`/users?${typed ? `q=${q}&` : ""}size=${size}`).catch(() => ({ data: { data: [] as Recipient[] } })),
          api.get<{ id: number; name: string; email: string }[]>(`/mail/contacts/${typed ? `?q=${q}` : ""}`).catch(() => ({ data: [] })),
        ]);
        const users = (ur.data?.data || []) as Recipient[];
        // negative ids so address-book entries never collide with user ids
        const contacts = (Array.isArray(cr.data) ? cr.data : []).map((c) => ({ id: -c.id, name: c.name, email: c.email }));
        const seen = new Set<string>();
        const merged: Recipient[] = [];
        for (const x of [...users, ...contacts]) {
          if (!x.email || seen.has(x.email)) continue;
          seen.add(x.email); merged.push(x);
        }
        setResults(merged.slice(0, input.trim() ? 8 : 200));
        setPopOpen(true);
      } catch {
        setResults([]); setPopOpen(false);
      }
    }, input.trim() ? 180 : 0);
    return () => clearTimeout(t);
  }, [input, popOpen]);

  // 한 번에 여러 개를 넣는다. 붙여넣기(쉼표·세미콜론·줄바꿈·공백 섞임)와 엑셀
  // 업로드가 모두 이 경로를 쓴다. 최대 개수를 넘으면 넘은 만큼은 버리고 알린다.
  // bulk=true 는 엑셀 업로드 — 손입력 상한(max)을 적용하지 않는다. 대량 발송이
  // 목적인데 10개에서 잘리면 업로드 기능 자체가 무의미해진다.
  const commitMany = (raw: string[], bulk = false) => {
    const cleaned = raw
      .flatMap((s) => s.split(/[,;\s]+/))
      .map((s) => s.trim().replace(/^[<"']|[>"']$/g, ""))
      .filter(Boolean);
    if (!cleaned.length) return;

    const next = [...value];
    let over = 0;
    for (const e of cleaned) {
      if (next.includes(e)) continue;         // 이미 있는 주소는 조용히 무시
      if (!bulk && max && next.length >= max) { over++; continue; }
      next.push(e);
    }
    if (next.length !== value.length) onChange(next);
    setInput("");
    setResults([]);
    setPopOpen(false);
    if (over) toast.warn(`${label}은 최대 ${max}개까지입니다`, `${over}개는 추가하지 못했습니다`);
  };

  const commit = (email: string) => commitMany([email]);

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === "," || e.key === ";") {
      e.preventDefault();
      // 직접 완전한 주소를 입력했다면 그것을 우선한다. 드롭다운이 떠 있다고 해서
      // 추천 항목을 넣어 버리면, 두 번째 주소부터 엉뚱한 값이 들어가거나 이미 있는
      // 주소로 걸러져 "더 추가가 안 되는" 것처럼 보인다.
      const typed = input.trim();
      if (typed.includes("@")) commit(typed);
      else if (popOpen && results[highlight]) commit(results[highlight].email);
      else commit(typed);
    } else if (e.key === "Backspace" && !input && value.length) {
      onChange(value.slice(0, -1));
    } else if (e.key === "ArrowDown" && popOpen) {
      e.preventDefault(); setHighlight((h) => Math.min(results.length - 1, h + 1));
    } else if (e.key === "ArrowUp" && popOpen) {
      e.preventDefault(); setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === "Escape") {
      setPopOpen(false);
    }
  };

  return (
    <div ref={fieldRef} className={`field ${chipOver ? "chip-drop-target" : ""}`} style={{ position: "relative", flexWrap: "wrap" }}
      onDragOver={onChipDragOver} onDragEnter={onChipDragOver} onDragLeave={onChipDragLeave} onDrop={onChipDrop}>
      <span className="key">{label}</span>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, flex: 1, alignItems: "center" }}>
        {value.map((v) => (
          <span key={v} className={`t-pill info ${boxId ? "recip-chip" : ""}`} style={{ padding: "2px 8px", fontSize: 11.5 }}
            draggable={!!boxId}
            title={boxId ? "끌어서 받는사람·참조·숨은참조 칸으로 옮길 수 있습니다" : undefined}
            onDragStart={(e) => {
              if (!boxId) return;
              e.dataTransfer.setData(CHIP_DND_TYPE, JSON.stringify({ email: v, from: boxId }));
              e.dataTransfer.setData("text/plain", v);   // 밖(메모장 등)에 떨어뜨리면 주소 글자가 들어가도록
              e.dataTransfer.effectAllowed = "move";
            }}>
            {v}
            <button onClick={() => onChange(value.filter((x) => x !== v))} style={{ marginLeft: 4, background: "transparent", border: "none", color: "inherit", cursor: "pointer" }}>×</button>
          </span>
        ))}
        <input
          ref={inputRef}
          value={input}
          onChange={(e) => { setInput(e.target.value); setHighlight(0); }}
          onFocus={() => setPopOpen(true)}
          onBlur={() => { setTimeout(() => setPopOpen(false), 120); if (input.includes("@")) commit(input); }}
          onKeyDown={onKey}
          // 여러 주소를 한꺼번에 붙여넣는 일이 흔하다 — 쉼표·줄바꿈으로 끊어 모두 넣는다.
          onPaste={(e) => {
            const text = e.clipboardData.getData("text");
            if (/[,;\s]/.test(text.trim())) { e.preventDefault(); commitMany([text]); }
          }}
          placeholder={value.length === 0 ? "이메일 또는 이름 (여러 개 붙여넣기 가능)" : ""}
          style={{ minWidth: 100 }}
        />
        {extra}
        {onBulk && <BulkUpload onLoaded={(list) => commitMany(list, true)} />}
        {value.length > 0 && (
          <span style={{ fontSize: 11, color: "var(--text-3)", marginLeft: "auto", whiteSpace: "nowrap" }}>
            {value.length}{max ? ` / ${max}` : ""}명
            {value.length > 1 && (
              <button onClick={() => onChange([])}
                style={{ marginLeft: 6, background: "transparent", border: "none", color: "var(--text-3)", cursor: "pointer", textDecoration: "underline", fontSize: 11 }}>
                전체 지우기
              </button>
            )}
          </span>
        )}
      </div>
      {popOpen && results.length > 0 && (
        <div className="recip-pop" style={{ top: popPos.top, left: popPos.left }}>
          {results.map((r, i) => (
            <div key={r.id} className={`item ${i === highlight ? "sel" : ""}`}
                 onMouseEnter={() => setHighlight(i)}
                 onMouseDown={(e) => { e.preventDefault(); commit(r.email); }}>
              {r.name}
              <span className="em">{r.email}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
